"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Stethoscope, Sparkles, ArrowRight, FileText, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="intro" data-nav="light" className="relative bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }}>
          {/* Left Column: Editorial Copy & Clinical Badges */}
          <div className="lg:col-span-7 flex flex-col space-y-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2.5">
              <Badge variant="outline" className="bg-muted text-foreground border-border px-3 py-1 font-sans text-xs tracking-wider uppercase font-semibold">
                <Stethoscope className="size-3.5 text-foreground mr-1.5" />
                Clinical Reference Library
              </Badge>
              <span className="text-xs text-muted-foreground font-sans">
                Updated weekly with peer-reviewed veterinary studies
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
              Pet health & symptom guidance
            </motion.h1>

            <motion.p variants={itemVariants} className="font-sans text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl font-normal">
              Understand symptoms, preventive care and common health concerns with clear, evidence-informed guidance.
            </motion.p>

            {/* Clinical Governance Callout (Assurance Box) */}
            <motion.div variants={itemVariants} className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-xs relative overflow-hidden">
              <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-foreground" />
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-muted text-foreground shrink-0 mt-0.5">
                  <ShieldCheck className="size-5" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <h3 className="font-sans text-base font-semibold text-card-foreground">
                      Veterinary review standards
                    </h3>
                    <Badge variant="secondary" className="text-[11px] font-medium bg-muted text-muted-foreground px-2 py-0.5">
                      Evidence-informed
                    </Badge>
                  </div>
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                    Every article and symptom evaluation on PetsWhirl is drafted against published clinical research and verified by licensed veterinary medical advisors.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quick Navigation / Explore Actions */}
            <motion.div variants={itemVariants} className="pt-2 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="rounded-lg shadow-sm font-semibold group">
                <Link to="/DogsPage">
                  Dog health guides
                  <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg font-semibold">
                <Link to="/CatsPage">
                  Cat health guides
                </Link>
              </Button>

              <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <Link to="/AboutPetsWhirlPage">
                  <FileText className="size-4 mr-1.5" />
                  Our editorial process
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: Documentary Photography & Data Points */}
          <motion.div variants={itemVariants} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Primary Documentary Image */}
              <div className="relative rounded-2xl overflow-hidden border border-border bg-muted shadow-md">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/veterinarian_examining_dog_patient.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/veterinarian_examining_dog_patient.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/veterinarian_examining_dog_patient.webp-wvc-sizes" alt="Veterinarian conducting a gentle clinical checkup on a golden retriever" className="w-full h-auto aspect-[4/5] object-cover object-center" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent pointer-events-none" />
                
                {/* Image Overlay Caption */}
                <div className="absolute bottom-4 left-4 right-4 p-3.5 rounded-xl bg-background/95 backdrop-blur-sm border border-border text-foreground shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="size-2 rounded-full bg-foreground animate-pulse" />
                    <span className="font-sans text-xs font-semibold">Preventive routine care</span>
                  </div>
                  <span className="font-sans text-xs text-muted-foreground">Clinical examination</span>
                </div>
              </div>

              {/* Floating Verification Note */}
              <div className="hidden sm:flex absolute -bottom-6 -left-6 rounded-xl border border-border bg-card p-4 shadow-lg max-w-[240px] items-center gap-3">
                <div className="size-9 rounded-lg bg-muted flex items-center justify-center text-foreground shrink-0">
                  <AlertCircle className="size-5" />
                </div>
                <div>
                  <p className="font-sans text-xs font-semibold text-card-foreground">Symptom clarity</p>
                  <p className="font-sans text-[11px] text-muted-foreground leading-tight">When to consult your veterinarian promptly</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}